import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ResponsiveContainer, ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine
} from 'recharts';
import { fetchMachineSpc, fetchMachinePph, fetchMachineQualitySummary } from '../../../services/api';
import { useAutoRefresh } from '../../../hooks/useAutoRefresh';
import { useTranslation } from '../../../context/LanguageContext';
import './SpcNew.css';

/* ===== 품질·생산 > 공정관리 (SPC) — Figma「사출기 > 품질 생산 > 공정관리」새 디자인 =====
 * 검수사진 탭(InspectionPhotos)의 서브 탭으로 렌더된다. 스타일은 sp- 접두어만 쓴다.
 * 구성은 기존 SPC 탭과 같다(측정 항목 선택 → X̄ 관리도 / R 관리도 / 공정능력지수 / 기초통계 / 이상점 검출)에
 * 시안의 생산 요약을 더했다. 측정 항목은 기존 SPC 탭과 같이 셀렉트로 고른다(2026-09-28 요청).
 *
 * 데이터 출처
 * - 관리도·통계·이상점: /dashboard/machine/{id}/spc?parameter=… — X̄/R 관리도(그룹 평균·범위·UCL·CL·LCL·그룹 시각),
 *                       Cp/Cpk/Pp/Ppk/부적합률, 기초통계, 이상점 목록(최근 10건), 부분군 크기(sampleSize)
 *                       SPC 설정(spc_configuration)이 없는 설비·항목은 백엔드가 오류를 내므로 안내 문구로 대체한다.
 *                       금형온도는 백엔드에 수집 데이터가 없어(항상 오류) 탭을 비활성으로 둔다.
 * - 생산량·불량·양품률: /machine/quality-summary/{id} — 오늘 shot_time 기준 샷 수 / GOOD / BAD 집계. 양품률은 판정된 샷 기준
 * - PPH             : /machine/pph/{id} — 오늘 시간대별 샷 수. 생산이 있었던 시간대의 평균으로 계산해 "추정" 표기
 *
 * 관리도의 "이탈" 표식은 그룹 값이 UCL/LCL 을 벗어난 점에 붙인다(화면에서 판정). 값이 null 이면 '—'. */

const isNum = (v) => v != null && !Number.isNaN(Number(v));
const num = (v, digits = 2) => (isNum(v) ? Number(v).toFixed(digits) : '—');
const intFmt = (v) => (isNum(v) ? Number(v).toLocaleString() : '—');
/* 백엔드는 표준편차가 0 이면 Cp/Cpk/Pp/Ppk 를 999.99 로 내려준다 — 의미 있는 값이 아니라 '—' 로 둔다 */
const capability = (v) => (isNum(v) && Number(v) < 999 ? Number(v).toFixed(2) : '—');
const localDateStr = (d = new Date()) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/* 측정 항목 — 백엔드 파라미터 이름 ↔ 기존 SPC 탭 번역 키. 순서·항목은 기존 SPC 탭 셀렉트와 같다 */
const PARAMS = [
  { key: 'cycle_time', labelKey: 'cycleTime' },
  { key: 'injection_pressure', labelKey: 'injectionPressure' },
  { key: 'injection_speed', labelKey: 'injectionSpeed' },
  { key: 'mold_temperature', labelKey: 'moldTemperature', disabled: true }, /* 백엔드 데이터 없음 */
  { key: 'cushion', labelKey: 'cushion' }
];

const COLORS = { xbar: '#47CD89', range: '#83A6FF', limit: '#83A6FF', center: '#98A8BF', out: '#F04438' };
const TOOLTIP_STYLE = {
  contentStyle: { backgroundColor: '#1A1E24', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '4px', fontSize: '12px' },
  labelStyle: { color: '#98A8BF', fontSize: '12px' }
};
const AXIS_TICK = { fill: '#98A8BF', fontSize: 11 };
const AXIS_LINE = { stroke: 'rgba(255,255,255,0.12)' };

/* 관리도 응답(group, value, ucl, cl, lcl) → 차트 행. 백엔드 그룹 1 = 최신이라 시간이 왼쪽→오른쪽으로 흐르도록 뒤집는다.
 * timeByGroup: X̄ 응답의 그룹 시각(R 응답에는 시각이 없어 같은 그룹 번호로 맞춘다) */
function toChartRows(rows, timeByGroup) {
  return [...(rows || [])].reverse().map((d) => {
    const value = isNum(d.value) ? Number(d.value) : null;
    const ucl = isNum(d.ucl) ? Number(d.ucl) : null;
    const lcl = isNum(d.lcl) ? Number(d.lcl) : null;
    return {
      group: d.group,
      time: d.time || timeByGroup?.[d.group] || `#${d.group}`,
      value,
      ucl,
      cl: isNum(d.cl) ? Number(d.cl) : null,
      lcl,
      out: value != null && ucl != null && lcl != null && (value > ucl || value < lcl)
    };
  });
}

function domainOf(rows) {
  const vals = rows.flatMap((d) => [d.value, d.ucl, d.lcl]).filter(isNum);
  if (vals.length === 0) return ['auto', 'auto'];
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const pad = (max - min) * 0.15 || 1;
  return [Number((min - pad).toFixed(2)), Number((max + pad).toFixed(2))];
}

function Card({ label, value, unit, tag }) {
  return (
    <div className="sp-card">
      <span className="sp-card-label">
        {label}
        {tag && <em className="sp-tag">{tag}</em>}
      </span>
      <span className="sp-card-value">
        {value}
        {unit && value !== '—' && <small>{unit}</small>}
      </span>
    </div>
  );
}

/* 관리도 — UCL/CL/LCL 점선 + 추이선 + 이탈 점(빨간 점 + "이탈 값" 라벨) */
function ControlChart({ rows, color, name, unit, outLabel }) {
  const limits = rows[0] || null;
  const yDomain = useMemo(() => domainOf(rows), [rows]);
  const renderDot = (props) => {
    const { cx, cy, payload, index } = props;
    if (!payload?.out || !isNum(cx) || !isNum(cy)) return <g key={`dot-${index}`} />;
    return (
      <g key={`dot-${index}`}>
        <circle cx={cx} cy={cy} r={4} fill={COLORS.out} />
        <text x={cx + 8} y={cy - 8} fill="#FFFFFF" fontSize={12}>{outLabel} {num(payload.value)}</text>
      </g>
    );
  };
  const fmt = (v) => (isNum(v) ? `${Number(v).toFixed(2)}${unit ? ` ${unit}` : ''}` : '—');
  return (
    <div className="sp-chart">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={rows} margin={{ top: 24, right: 16, bottom: 4, left: 0 }}>
          <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="time" tick={AXIS_TICK} axisLine={AXIS_LINE} tickLine={false} interval="preserveStartEnd" minTickGap={24} />
          <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} domain={yDomain} width={56} unit={unit ? ` ${unit}` : ''} />
          <Tooltip {...TOOLTIP_STYLE} formatter={(v, n) => [fmt(v), n]} />
          {/* 관리한계선 — 백엔드가 모든 그룹에 같은 값을 내려주므로 첫 행의 값을 쓴다 */}
          {isNum(limits?.ucl) && (
            <ReferenceLine y={limits.ucl} stroke={COLORS.limit} strokeDasharray="4 3" label={{ value: `UCL ${num(limits.ucl)}`, position: 'insideTopLeft', fill: '#C5D7E0', fontSize: 11 }} />
          )}
          {isNum(limits?.cl) && (
            <ReferenceLine y={limits.cl} stroke={COLORS.center} strokeDasharray="4 3" label={{ value: `CL ${num(limits.cl)}`, position: 'insideTopLeft', fill: '#C5D7E0', fontSize: 11 }} />
          )}
          {isNum(limits?.lcl) && (
            <ReferenceLine y={limits.lcl} stroke={COLORS.limit} strokeDasharray="4 3" label={{ value: `LCL ${num(limits.lcl)}`, position: 'insideTopLeft', fill: '#C5D7E0', fontSize: 11 }} />
          )}
          <Line type="linear" dataKey="value" name={name} stroke={color} strokeWidth={1.5} dot={renderDot} activeDot={{ r: 4 }} isAnimationActive={false} connectNulls />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function SpcNew({ equipmentId }) {
  const { t } = useTranslation();
  const tr = useCallback((key, params) => t(`pages.monitoring.machine.spcNew.${key}`, params), [t]);
  const ts = useCallback((key) => t(`pages.monitoring.machine.spc.${key}`), [t]);   /* 기존 SPC 탭 번역 재사용 */

  const [parameter, setParameter] = useState('cycle_time');
  const [spc, setSpc] = useState(null);
  const [spcError, setSpcError] = useState(null);
  const [pph, setPph] = useState(null);
  const [quality, setQuality] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (silent) => {
    if (!equipmentId) return;
    if (!silent) setIsLoading(true);
    setError(null);
    const today = localDateStr();
    const results = await Promise.allSettled([
      fetchMachineSpc(equipmentId, parameter),
      fetchMachinePph(equipmentId, today),
      fetchMachineQualitySummary(equipmentId, today)
    ]);
    const [spcRes, pphRes, qualityRes] = results;

    /* SPC 는 설정이 없는 설비·항목에서 정상적으로 실패할 수 있어 별도 안내로 처리한다 */
    if (spcRes.status === 'fulfilled') {
      setSpc(spcRes.value);
      setSpcError(null);
    } else {
      console.error('SPC(new) load failed:', parameter, spcRes.reason);
      setSpc(null);
      setSpcError(tr('spcUnavailable'));
    }
    if (pphRes.status === 'fulfilled') setPph(pphRes.value);
    else console.error('SPC(new) pph load failed:', pphRes.reason);
    if (qualityRes.status === 'fulfilled') setQuality(qualityRes.value);
    else console.error('SPC(new) quality summary load failed:', qualityRes.reason);
    if (pphRes.status === 'rejected' && qualityRes.status === 'rejected') setError(tr('loadFailed'));
    if (!silent) setIsLoading(false);
  }, [equipmentId, parameter, tr]);

  useEffect(() => { load(false); }, [load]);
  useAutoRefresh(() => load(true), !!equipmentId);

  const selectParameter = (key) => {
    const p = PARAMS.find((x) => x.key === key);
    if (!p || p.disabled || p.key === parameter) return;
    setSpc(null);
    setSpcError(null);
    setParameter(p.key);
  };

  /* ---------------- 관리도 파생 ---------------- */
  /* 응답 키 주의: DTO 필드는 xBarChart / rChart 지만 Jackson 이 getXBarChart / getRChart 를 "xbarChart" / "rchart" 로 직렬화한다
   * (기존 SPC 탭도 spcData.xbarChart 를 쓴다). 혹시 설정이 바뀌어도 동작하도록 두 이름 모두 본다 */
  const xbarData = spc?.xbarChart?.data || spc?.xBarChart?.data;
  const rData = spc?.rchart?.data || spc?.rChart?.data;
  const xbarRows = useMemo(() => toChartRows(xbarData), [xbarData]);
  const rRows = useMemo(() => {
    const timeByGroup = Object.fromEntries((xbarData || []).map((d) => [d.group, d.time]));
    return toChartRows(rData, timeByGroup);
  }, [xbarData, rData]);

  const paramDef = PARAMS.find((p) => p.key === parameter) || PARAMS[0];
  const paramLabel = spc?.parameterLabel || ts(`params.${paramDef.labelKey}`);
  const unit = spc?.unit || '';
  const cap = spc?.statistics?.processCapability || null;
  const basic = spc?.statistics?.basicStats || null;
  const anomalies = spc?.anomalies || [];
  const hasChart = !spcError && xbarRows.length > 0;

  /* ---------------- 생산 요약 파생 ---------------- */
  /* PPH = 오늘 생산량 ÷ 생산이 있었던 시간대 수 (추정). 시간대별 데이터가 없으면 '—' */
  const pphValue = useMemo(() => {
    const hours = (pph?.hourlyData || []).filter((h) => isNum(h?.count) && Number(h.count) > 0);
    if (hours.length === 0) return null;
    const total = hours.reduce((a, h) => a + Number(h.count), 0);
    return Math.round(total / hours.length);
  }, [pph]);

  const renderSpcState = () => {
    if (isLoading && !spc && !spcError) return <div className="sp-message">{tr('loading')}</div>;
    if (spcError) return <div className="sp-message">{spcError}</div>;
    if (spc && xbarRows.length === 0) return <div className="sp-message">{tr('noChart')}</div>;
    return null;
  };

  return (
    <div className="sp-root">
      {/* 측정 항목 셀렉트 — 기존 SPC 탭과 같은 항목·순서. 금형온도는 데이터가 없어 선택 불가 */}
      <div className="sp-controls">
        <label className="sp-field">
          <span className="sp-field-label">{ts('measurementItem')}</span>
          <select className="sp-select" value={parameter} onChange={(e) => selectParameter(e.target.value)}>
            {PARAMS.map((p) => (
              <option key={p.key} value={p.key} disabled={p.disabled} title={p.disabled ? tr('moldTempNoData') : undefined}>
                {ts(`params.${p.labelKey}`)}{p.disabled ? ` (${tr('noData')})` : ''}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* 관리도 2종 — 기존 SPC 탭과 같은 X̄ / R 관리도 + 이상점 검출(오른쪽 좁은 열).
          화면 맞춤(2026-10-28): 이 행이 남은 높이를 채우고, 아래 지표 3패널은 한 줄로 둔다 */}
      <div className="sp-grid-2 sp-charts-row">
        <section className="sp-panel">
          <div className="sp-panel-head">
            <h3 className="sp-panel-title">{tr('monitoring', { p: paramLabel })}</h3>
            <span className="sp-note">{ts('xbarChart')}</span>
          </div>
          {renderSpcState()}
          {hasChart && (
            <>
              <ControlChart rows={xbarRows} color={COLORS.xbar} name={paramLabel} unit={unit} outLabel={tr('outOfControl')} />
              <div className="sp-note">{tr('chartNote')}</div>
            </>
          )}
        </section>
        <section className="sp-panel">
          <div className="sp-panel-head">
            <h3 className="sp-panel-title">{ts('rChart')}</h3>
            <span className="sp-note">{ts('range')}</span>
          </div>
          {renderSpcState()}
          {hasChart && (
            <>
              <ControlChart rows={rRows} color={COLORS.range} name={ts('range')} unit={unit} outLabel={tr('outOfControl')} />
              <div className="sp-note">{tr('rChartNote')}</div>
            </>
          )}
        </section>

        {/* 이상점 검출 — 백엔드가 UCL/LCL 위반 샷을 최근 10건까지 내려준다 */}
        <section className="sp-panel sp-anomaly-panel">
          <div className="sp-panel-head">
            <h3 className="sp-panel-title">{ts('anomalyDetection')}</h3>
            {anomalies.length > 0 && <span className="sp-note">{tr('anomalyCount', { n: anomalies.length })}</span>}
          </div>
          {anomalies.length === 0 ? (
            <div className="sp-message sp-message-short">{spcError ? spcError : ts('noAnomaly')}</div>
          ) : (
            <div className="sp-scroll">
              <table className="sp-table">
                <thead>
                  <tr>
                    <th>{ts('anomalyTime')}</th>
                    <th>{ts('anomalyValue')}</th>
                    <th>{ts('anomalyType')}</th>
                  </tr>
                </thead>
                <tbody>
                  {anomalies.map((a, i) => (
                    <tr key={`${a.time}-${i}`}>
                      <td>{a.time || '—'}</td>
                      <td className="sp-table-num">{num(a.value)}{unit && isNum(a.value) ? ` ${unit}` : ''}</td>
                      <td className="sp-table-warn">{a.type || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      <div className="sp-bottom-row">
      {/* 공정 능력지수 — Cp/Cpk/Pp/Ppk/부적합률 + 부분군 크기 */}
      <section className="sp-panel">
        <div className="sp-panel-head">
          <h3 className="sp-panel-title">{tr('capabilityTitle')}</h3>
        </div>
        <div className="sp-cards sp-cards-6">
          <Card label={tr('cp')} value={capability(cap?.cp)} />                        {/* spc.statistics.processCapability.cp */}
          <Card label={tr('cpk')} value={capability(cap?.cpk)} />                      {/* spc.statistics.processCapability.cpk */}
          <Card label={tr('pp')} value={capability(cap?.pp)} />                        {/* spc.statistics.processCapability.pp */}
          <Card label={tr('ppk')} value={capability(cap?.ppk)} />                      {/* spc.statistics.processCapability.ppk */}
          <Card label={tr('defectRate')} value={num(cap?.defectRate)} unit="%" />      {/* processCapability.defectRate (규격 외 샘플 비율) */}
          <Card label={tr('subgroupSize')} value={isNum(spc?.sampleSize) ? `N=${spc.sampleSize}` : '—'} /> {/* spc.sampleSize */}
        </div>
      </section>

      {/* 기초통계 */}
      <section className="sp-panel">
        <div className="sp-panel-head">
          <h3 className="sp-panel-title">{ts('basicStats')}</h3>
        </div>
        <div className="sp-cards sp-cards-6">
          <Card label={ts('sampleCount')} value={intFmt(basic?.sampleCount)} />          {/* spc.statistics.basicStats.sampleCount */}
          <Card label={ts('mean')} value={num(basic?.mean)} unit={unit} />               {/* basicStats.mean */}
          <Card label={ts('stdDev')} value={num(basic?.stdDev)} unit={unit} />           {/* basicStats.stdDev */}
          <Card label={ts('min')} value={num(basic?.min)} unit={unit} />                 {/* basicStats.min */}
          <Card label={ts('max')} value={num(basic?.max)} unit={unit} />                 {/* basicStats.max */}
          <Card label={ts('range')} value={num(basic?.range)} unit={unit} />             {/* basicStats.range */}
        </div>
      </section>

      {/* 생산 요약 (시안 추가 항목) */}
      <section className="sp-panel">
        <div className="sp-panel-head">
          <h3 className="sp-panel-title">{tr('productionTitle')}</h3>
          <span className="sp-note">{tr('productionNote', { d: quality?.date || localDateStr() })}</span>
        </div>
        {error && <div className="sp-message sp-message-error">{error}</div>}
        <div className="sp-cards">
          <Card label={tr('production')} value={intFmt(quality?.totalShots)} unit={tr('unitShot')} />       {/* quality-summary.totalShots */}
          <Card label={tr('goodRate')} value={num(quality?.goodRate, 1)} unit="%" />                          {/* quality-summary.goodRate (판정 샷 기준) */}
          <Card label={tr('pph')} value={intFmt(pphValue)} unit={tr('unitShotPerHour')} tag={tr('estimated')} /> {/* pph.hourlyData 평균 */}
          <Card label={tr('defects')} value={intFmt(quality?.badCount)} unit={tr('unitShot')} />              {/* quality-summary.badCount */}
        </div>
      </section>
      </div>
    </div>
  );
}

export default SpcNew;
